import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { fetchAvailability, parseAvailability } from '../services/supabaseClient';
import { 
  TechBackground, 
  CircuitLines, 
  TechParticles, 
  HolographicScanner,
  BorderGlow,
  TechButton,
  TechTitle,
  SectionTitle,
  TechGridContainer,
  TechLoadingSpinner,
  TechSubmitButton,
  TechInput,
  TechLabel,
  TechFormGroup,
  TechFormContainer,
  gradientMove,
  dataProcessing,
  dataPulse,
  digitalDataFlow,
  rotate
} from '../styles/TechStylesModal';

// Animações básicas para o modal
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const modalFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const modalFadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const inputGlowPulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(0, 200, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 200, 255, 0.5);
  }
  100% {
    box-shadow: 0 0 10px rgba(0, 200, 255, 0.2);
  }
`;

// Animações para feedback do webhook
const successPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 200, 0.7);
    background-color: rgba(0, 255, 200, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(0, 255, 200, 0);
    background-color: rgba(0, 255, 200, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 200, 0);
    background-color: rgba(0, 255, 200, 0.4);
  }
`;

const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 50, 50, 0.7);
    background-color: rgba(255, 50, 50, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(255, 50, 50, 0);
    background-color: rgba(255, 50, 50, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 50, 50, 0);
    background-color: rgba(255, 50, 50, 0.4);
  }
`;

const processingPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 150, 255, 0.7);
    background-color: rgba(0, 150, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(0, 150, 255, 0);
    background-color: rgba(0, 150, 255, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 150, 255, 0);
    background-color: rgba(0, 150, 255, 0.4);
  }
`;

const dataFlowAnimation = keyframes`
  0% {
    background-position: 0% 0%;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    background-position: 100% 100%;
    opacity: 0.3;
  }
`;

const techGridPulse = keyframes`
  0% {
    opacity: 0.3;
    transform: scale(0.95);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.3;
    transform: scale(0.95);
  }
`;

const cornerPulse = keyframes`
  0% {
    border-color: rgba(0, 200, 255, 0.3);
    box-shadow: 0 0 10px rgba(0, 200, 255, 0.3);
  }
  50% {
    border-color: rgba(0, 200, 255, 0.8);
    box-shadow: 0 0 20px rgba(0, 200, 255, 0.8);
  }
  100% {
    border-color: rgba(0, 200, 255, 0.3);
    box-shadow: 0 0 10px rgba(0, 200, 255, 0.3);
  }
`;

const iconRotate = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.2);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
`;

const textReveal = keyframes`
  0% {
    clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
    opacity: 0;
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    opacity: 1;
  }
`;

// Estilos básicos do modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} 0.3s ease-in-out;
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0, 200, 255, 0.2);
  animation: ${props => props.isClosing ? modalFadeOut : modalFadeIn} 0.3s ease-in-out;
`;

const ModalBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(10, 15, 30, 0.9);
  border: 1px solid rgba(0, 200, 255, 0.3);
  z-index: 0;
`;

const ModalBody = styled.div`
  position: relative;
  z-index: 1;
  padding: 30px;
  width: 100%;
  height: 100%;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 200, 255, 0.5);
    border-radius: 4px;
    &:hover {
      background: rgba(0, 200, 255, 0.7);
    }
  }
`;

const ModalTitle = styled(TechTitle)`
  margin-top: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: rgba(0, 200, 255, 0.7);
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: rgba(0, 200, 255, 1);
    transform: scale(1.1);
  }
`;

// Componentes de formulário
const FormContainer = styled(TechFormContainer)`
  margin-top: 20px;
`;

const FormGroup = styled(TechFormGroup)``;

const Label = styled(TechLabel)``;

const Input = styled(TechInput)``;

const DateButton = styled.button`
  ${TechButton}
  padding: 10px 5px;
  flex: 1;
  min-width: 80px;
  animation: ${inputGlowPulse} 3s infinite ease-in-out;
  
  .day-name {
    display: block;
    font-weight: bold;
    margin-bottom: 5px;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 0.5px;
    font-size: 0.85em;
  }
`;

const TimeSlotButton = styled.button`
  ${TechButton}
  padding: 10px;
  font-family: 'Orbitron', sans-serif;
  letter-spacing: 0.5px;
  animation: ${inputGlowPulse} 3s infinite ease-in-out;
`;

const DateSelector = styled(TechGridContainer)`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 10px;
    background: transparent;
    border: 1px solid rgba(0, 200, 255, 0.2);
    z-index: -1;
    animation: ${inputGlowPulse} 3s infinite ease-in-out;
    pointer-events: none;
  }
`;

const TimeSlotContainer = styled(TechGridContainer)`
  padding: 10px 5px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 12px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 10px;
    background: transparent;
    border: 1px solid rgba(0, 200, 255, 0.2);
    z-index: -1;
    animation: ${inputGlowPulse} 3s infinite ease-in-out;
    pointer-events: none;
  }
`;

const SubmitButton = styled(TechSubmitButton)``;

const LoadingSpinner = styled(TechLoadingSpinner)``;

// Componentes para feedback visual
const FeedbackContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  backdrop-filter: blur(5px);
  transition: all 0.5s ease-in-out;
  overflow: hidden;
`;

const FeedbackBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.isSuccess ? 
      'linear-gradient(135deg, rgba(0, 50, 30, 0.9), rgba(0, 30, 60, 0.9))' : 
      'linear-gradient(135deg, rgba(50, 0, 30, 0.9), rgba(30, 0, 60, 0.9))'};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${props => props.isSuccess ? 
      'linear-gradient(45deg, transparent 95%, rgba(0, 255, 200, 0.8) 95%), linear-gradient(-45deg, transparent 95%, rgba(0, 255, 200, 0.8) 95%)' : 
      'linear-gradient(45deg, transparent 95%, rgba(255, 50, 50, 0.8) 95%), linear-gradient(-45deg, transparent 95%, rgba(255, 50, 50, 0.8) 95%)'};
    background-size: 30px 30px;
    animation: ${techGridPulse} 3s infinite ease-in-out;
  }
`;

const FeedbackIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  font-size: 36px;
  color: white;
  position: relative;
  animation: ${props => props.isSuccess ? successPulse : errorPulse} 2s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    border: 2px solid ${props => props.isSuccess ? 'rgba(0, 255, 200, 0.5)' : 'rgba(255, 50, 50, 0.5)'};
    animation: ${iconRotate} 8s infinite linear;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    border-radius: 50%;
    border: 1px dashed ${props => props.isSuccess ? 'rgba(0, 255, 200, 0.3)' : 'rgba(255, 50, 50, 0.3)'};
    animation: ${iconRotate} 12s infinite linear reverse;
  }
`;

const FeedbackMessage = styled.div`
  font-family: 'Orbitron', sans-serif;
  color: white;
  text-align: center;
  font-size: 24px;
  margin-bottom: 30px;
  text-shadow: 0 0 10px ${props => props.isSuccess ? 'rgba(0, 255, 200, 0.7)' : 'rgba(255, 50, 50, 0.7)'};
  letter-spacing: 1px;
  animation: ${textReveal} 1s forwards;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -20px;
    right: -20px;
    height: 2px;
    background: ${props => props.isSuccess ? 
      'linear-gradient(90deg, transparent, rgba(0, 255, 200, 0.7), transparent)' : 
      'linear-gradient(90deg, transparent, rgba(255, 50, 50, 0.7), transparent)'};
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: -20px;
    right: -20px;
    height: 2px;
    background: ${props => props.isSuccess ? 
      'linear-gradient(90deg, transparent, rgba(0, 255, 200, 0.7), transparent)' : 
      'linear-gradient(90deg, transparent, rgba(255, 50, 50, 0.7), transparent)'};
  }
`;

const DataLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${props => props.isSuccess ? 
      'linear-gradient(0deg, transparent 24%, rgba(0, 255, 200, 0.3) 25%, rgba(0, 255, 200, 0.3) 26%, transparent 27%, transparent 74%, rgba(0, 255, 200, 0.3) 75%, rgba(0, 255, 200, 0.3) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(0, 255, 200, 0.3) 25%, rgba(0, 255, 200, 0.3) 26%, transparent 27%, transparent 74%, rgba(0, 255, 200, 0.3) 75%, rgba(0, 255, 200, 0.3) 76%, transparent 77%, transparent)' : 
      'linear-gradient(0deg, transparent 24%, rgba(255, 50, 50, 0.3) 25%, rgba(255, 50, 50, 0.3) 26%, transparent 27%, transparent 74%, rgba(255, 50, 50, 0.3) 75%, rgba(255, 50, 50, 0.3) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(255, 50, 50, 0.3) 25%, rgba(255, 50, 50, 0.3) 26%, transparent 27%, transparent 74%, rgba(255, 50, 50, 0.3) 75%, rgba(255, 50, 50, 0.3) 76%, transparent 77%, transparent)'};
    background-size: 50px 50px;
    animation: ${dataFlowAnimation} 10s infinite linear;
  }
`;

const CornerElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ContinueButton = styled.button`
  ${TechButton}
  padding: 12px 30px;
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1.5px;
  margin-top: 20px;
  position: relative;
  overflow: hidden;
  color: white;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.8), 0 0 20px rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 5px;
    background: ${props => props.isSuccess ? 
      'linear-gradient(45deg, rgba(0, 255, 200, 0), rgba(0, 255, 200, 0.8), rgba(0, 255, 200, 0))' : 
      'linear-gradient(45deg, rgba(255, 50, 50, 0), rgba(255, 50, 50, 0.8), rgba(255, 50, 50, 0))'};
    background-size: 200% 200%;
    animation: ${cornerPulse} 3s infinite ease-in-out;
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: -1;
  }
`;

// Componentes para a tela de processamento
const ProcessingBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, 
    rgba(0, 20, 50, 0.9) 0%, 
    rgba(0, 40, 80, 0.8) 50%, 
    rgba(0, 20, 50, 0.9) 100%);
  background-size: 400% 400%;
  animation: ${gradientMove} 8s ease infinite;
  z-index: -1;
  border-radius: 10px;
`;

const ProcessingGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(90deg, rgba(0, 150, 255, 0.1) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 150, 255, 0.1) 1px, transparent 1px),
    linear-gradient(45deg, rgba(0, 150, 255, 0.05) 1px, transparent 1px),
    linear-gradient(-45deg, rgba(0, 150, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px, 20px 20px, 40px 40px, 40px 40px;
  z-index: 0;
  border-radius: 10px;
  animation: ${dataProcessing} 8s infinite ease-in-out;
`;

const ProcessingDataFlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(0deg, transparent 24%, 
      rgba(0, 150, 255, 0.3) 25%, 
      rgba(0, 150, 255, 0.3) 26%, 
      transparent 27%, 
      transparent 74%, 
      rgba(0, 150, 255, 0.3) 75%, 
      rgba(0, 150, 255, 0.3) 76%, 
      transparent 77%, transparent),
    linear-gradient(90deg, transparent 24%, 
      rgba(0, 150, 255, 0.3) 25%, 
      rgba(0, 150, 255, 0.3) 26%, 
      transparent 27%, 
      transparent 74%, 
      rgba(0, 150, 255, 0.3) 75%, 
      rgba(0, 150, 255, 0.3) 76%, 
      transparent 77%, transparent);
  background-size: 50px 50px;
  z-index: 0;
  border-radius: 10px;
  animation: ${digitalDataFlow} 15s infinite linear;
`;

const ProcessingCorners = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 50px;
    height: 50px;
    border-color: rgba(0, 150, 255, 0.5);
    animation: ${dataPulse} 4s infinite ease-in-out;
  }
  
  &::before {
    top: 20px;
    left: 20px;
    border-top: 2px solid;
    border-left: 2px solid;
    border-top-left-radius: 10px;
  }
  
  &::after {
    bottom: 20px;
    right: 20px;
    border-bottom: 2px solid;
    border-right: 2px solid;
    border-bottom-right-radius: 10px;
  }
`;

const ProcessingCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(0, 30, 60, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    border: 2px dashed rgba(0, 150, 255, 0.5);
    animation: ${rotate} 10s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    border: 1px solid rgba(0, 150, 255, 0.8);
    animation: ${dataPulse} 4s infinite ease-in-out;
  }
`;

const ProcessingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid transparent;
  border-top: 3px solid rgba(0, 200, 255, 0.8);
  border-right: 3px solid rgba(0, 200, 255, 0.6);
  border-bottom: 3px solid rgba(0, 200, 255, 0.4);
  border-left: 3px solid rgba(0, 200, 255, 0.2);
  border-radius: 50%;
  animation: ${rotate} 1.5s linear infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 200, 255, 0.4) 0%, transparent 70%);
    animation: ${dataPulse} 2s infinite ease-in-out;
  }
`;

const ProcessingText = styled.div`
  position: absolute;
  top: calc(50% + 80px);
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Orbitron', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: rgba(0, 200, 255, 0.9);
  text-shadow: 0 0 10px rgba(0, 200, 255, 0.5);
  letter-spacing: 2px;
  z-index: 2;
  text-align: center;
  
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 200, 255, 0.7), transparent);
    width: 120%;
    left: -10%;
  }
  
  &::before {
    top: -10px;
  }
  
  &::after {
    bottom: -10px;
  }
`;

const ProcessingDataPoints = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(0, 200, 255, 0.8);
    animation: ${dataPulse} 4s infinite ease-in-out alternate;
  }
  
  &::before {
    top: 30%;
    left: 20%;
  }
  
  &::after {
    bottom: 30%;
    right: 20%;
  }
`;

const ProcessingContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  overflow: hidden;
`;

// Componente principal do modal
const Modal = ({ isOpen, onClose, title, children }) => {
  const [isModalClosing, setIsModalClosing] = useState(false);
  const modalRef = useRef(null);
  
  // Fechar o modal com animação
  const closeWithAnimation = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      onClose();
      setIsModalClosing(false);
    }, 300); // Tempo da animação
  };
  
  // Fechar o modal ao clicar fora dele
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeWithAnimation();
    }
  };
  
  // Prevenir rolagem do body quando o modal estiver aberto
  useEffect(() => {
    if (isOpen) {
      // Salvar a posição atual de rolagem
      const scrollY = window.scrollY;
      
      // Desabilitar rolagem do body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restaurar rolagem e posição
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }, [isOpen]);
  
  // Fechar o modal ao pressionar ESC
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        closeWithAnimation();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen && !isModalClosing) return null;
  
  return (
    <ModalOverlay onClick={handleOverlayClick} isClosing={isModalClosing}>
      <ModalContent ref={modalRef} isClosing={isModalClosing}>
        <ModalBackground />
        
        {/* Elementos de fundo tecnológico */}
        <TechBackground />
        <CircuitLines />
        <BorderGlow />
        <TechParticles />
        <HolographicScanner />
        
        <ModalBody>
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={closeWithAnimation}>&times;</CloseButton>
          
          {children}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Componente do formulário de consulta
export const ConsultationForm = ({ onSubmit, isSubmitting }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackStatus, setFeedbackStatus] = useState(null); // 'success', 'error', 'processing' ou null
  
  // Buscar disponibilidade ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const productId = 'PmQn6l5kpfCBhF5Z';
        const availabilityData = await fetchAvailability(productId);
        
        if (availabilityData) {
          const parsed = parseAvailability(availabilityData.disponibilidade, availabilityData.agendamentos);
          setAvailability(parsed);
        } else {
          setError('Não foi possível carregar os horários disponíveis.');
        }
      } catch (error) {
        console.error('Erro ao buscar disponibilidade:', error);
        setError('Ocorreu um erro ao carregar os horários disponíveis.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Função para lidar com a seleção de data
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null); // Resetar o horário selecionado quando mudar a data
  };
  
  // Função para lidar com a seleção de horário
  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
  };
  
  // Função para fechar o feedback
  const handleCloseFeedback = () => {
    if (feedbackStatus === 'success') {
      // Se foi sucesso, envia os dados para o componente pai
      const formData = {
        name,
        email,
        phone,
        date: selectedDate,
        timeSlot: selectedTimeSlot
      };
      onSubmit(formData);
      
      // Rolar para o topo da página
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Se foi erro, apenas fecha o feedback
      setFeedbackStatus(null);
    }
  };
  
  // Função para lidar com o envio do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    if (!name || !email || !phone || !selectedDate || !selectedTimeSlot) {
      const missingFields = [];
      if (!name) missingFields.push('nome');
      if (!email) missingFields.push('email');
      if (!phone) missingFields.push('telefone');
      if (!selectedDate) missingFields.push('data');
      if (!selectedTimeSlot) missingFields.push('horário');
      
      console.log(`Campos obrigatórios não preenchidos: ${missingFields.join(', ')}.`);
      return;
    }
    
    // Preparar os dados para envio
    const formData = {
      name,
      email,
      phone,
      date: selectedDate,
      timeSlot: selectedTimeSlot
    };
    
    // Mostrar tela de processamento
    setFeedbackStatus('processing');
    
    try {
      // Enviar dados para o webhook
      const response = await fetch('https://webhook-editor.abianca.com.br/webhook/4aa34526-80df-474c-84cb-a3f90f1fe767', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error(`Erro na requisição: ${response.status}`);
      }
      
      // Processar a resposta do webhook
      const responseText = await response.text();
      
      if (responseText.includes('sucesso')) {
        console.log('Dados enviados com sucesso para o webhook!');
        setFeedbackStatus('success');
      } else if (responseText.includes('fracasso')) {
        console.error('O webhook retornou fracasso:', responseText);
        setFeedbackStatus('error');
      } else {
        console.warn('Resposta inesperada do webhook:', responseText);
        setFeedbackStatus('error');
      }
    } catch (error) {
      console.error('Erro ao enviar dados para o webhook:', error);
      setFeedbackStatus('error');
    }
  };
  
  // Função para formatar o número de telefone
  const formatPhoneNumber = (value) => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a formatação conforme o número de dígitos
    if (numbers.length <= 2) {
      return numbers.length ? `(${numbers}` : '';
    } else if (numbers.length <= 7) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
    } else {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
    }
  };
  
  // Renderizar os botões de data
  const renderDateButtons = () => {
    if (loading) return <p>Carregando datas disponíveis...</p>;
    if (error) return <p>{error}</p>;
    if (Object.keys(availability).length === 0) return <p>Não há horários disponíveis no momento.</p>;
    
    // Filtrar datas que não têm horários disponíveis
    const datesWithSlots = Object.entries(availability)
      .filter(([_, slots]) => slots.length > 0)
      .map(([date]) => date);
    
    if (datesWithSlots.length === 0) return <p>Não há horários disponíveis no momento.</p>;
    
    return (
      <DateSelector>
        {datesWithSlots.map((date) => (
          <DateButton
            key={date}
            onClick={() => handleDateSelect(date)}
            selected={selectedDate === date}
            type="button" // Especificando o tipo como button para evitar submissão do formulário
          >
            <span className="day-name">{date}</span>
          </DateButton>
        ))}
      </DateSelector>
    );
  };
  
  // Renderizar os botões de horário
  const renderTimeSlotButtons = () => {
    if (!selectedDate) return null;
    
    const timeSlots = availability[selectedDate] || [];
    
    if (timeSlots.length === 0) return <p>Não há horários disponíveis para esta data.</p>;
    
    return (
      <TimeSlotContainer>
        {timeSlots.map((timeSlot, index) => {
          const displayTime = timeSlot.split('-')[0];
          return (
            <TimeSlotButton
              key={index}
              onClick={() => handleTimeSlotSelect(timeSlot)}
              selected={selectedTimeSlot === timeSlot}
              type="button" // Especificando o tipo como button para evitar submissão do formulário
            >
              {displayTime}
            </TimeSlotButton>
          );
        })}
      </TimeSlotContainer>
    );
  };
  
  // Renderizar o feedback visual
  const renderFeedback = () => {
    if (!feedbackStatus) return null;
    
    const isSuccess = feedbackStatus === 'success';
    const isProcessing = feedbackStatus === 'processing';
    
    return (
      <FeedbackContainer>
        {isProcessing ? (
          <ProcessingContainer>
            <ProcessingBackground />
            <ProcessingGrid />
            <ProcessingDataFlow />
            <ProcessingCorners />
            <ProcessingCircle>
              <ProcessingSpinner />
            </ProcessingCircle>
            <ProcessingText>PROCESSANDO AGENDAMENTO...</ProcessingText>
            <ProcessingDataPoints />
          </ProcessingContainer>
        ) : (
          <>
            <FeedbackBackground isSuccess={isSuccess} />
            <DataLines isSuccess={isSuccess} />
            <CornerElements isSuccess={isSuccess} />
            
            <FeedbackIcon isSuccess={isSuccess}>
              {isSuccess ? '✓' : '✕'}
            </FeedbackIcon>
            
            <FeedbackMessage isSuccess={isSuccess}>
              {isSuccess 
                ? 'AGENDAMENTO REALIZADO COM SUCESSO' 
                : 'FALHA NO PROCESSAMENTO DO AGENDAMENTO'}
            </FeedbackMessage>
            
            <ContinueButton 
              isSuccess={isSuccess}
              onClick={handleCloseFeedback}
              type="button"
            >
              {isSuccess ? 'VOLTAR PARA PÁGINA' : 'TENTAR NOVAMENTE'}
            </ContinueButton>
          </>
        )}
      </FeedbackContainer>
    );
  };
  
  return (
    <FormContainer onSubmit={handleSubmit}>
      {renderFeedback()}
      
      <FormGroup>
        <Label htmlFor="name">Nome*</Label>
        <Input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={isSubmitting || feedbackStatus !== null}
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="email">Email*</Label>
        <Input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isSubmitting || feedbackStatus !== null}
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="phone">Telefone*</Label>
        <Input
          type="tel"
          id="phone"
          value={phone}
          onChange={(e) => {
            // Aplica a formatação e atualiza o estado
            const formattedValue = formatPhoneNumber(e.target.value);
            setPhone(formattedValue);
          }}
          required
          disabled={isSubmitting || feedbackStatus !== null}
          maxLength={15}
        />
      </FormGroup>
      
      <FormGroup>
        <SectionTitle>Selecione uma data*</SectionTitle>
        {renderDateButtons()}
      </FormGroup>
      
      {selectedDate && (
        <FormGroup>
          <SectionTitle>Selecione um horário*</SectionTitle>
          {renderTimeSlotButtons()}
        </FormGroup>
      )}
      
      <SubmitButton 
        type="submit" 
        disabled={isSubmitting || feedbackStatus !== null || !name || !email || !phone || !selectedDate || !selectedTimeSlot}
        title={(!name || !email || !phone || !selectedDate || !selectedTimeSlot) ? 
          "Preencha todos os campos obrigatórios e selecione uma data e horário" : 
          "Enviar agendamento"}
      >
        {isSubmitting ? (
          <>
            Enviando <LoadingSpinner />
          </>
        ) : (
          'AGENDAR CONSULTORIA'
        )}
      </SubmitButton>
    </FormContainer>
  );
};

export default Modal;
